import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { ArrowLeft, BookOpen, Settings, Loader2, Trash2, LogOut, User, Save, Mail } from 'lucide-react';
import { useProfileManagement } from '@/hooks/useProfileManagement';
import { useAuth } from '@/contexts/AuthContext';
import BiographyEditor from '@/components/BiographyEditor';
import SmartWritingAssistant from '@/components/SmartWritingAssistant';
import WritingPrompts from '@/components/WritingPrompts';
import BiographyQualityPanel from '@/components/BiographyQualityPanel';
import { ReferralCodeInput } from '@/components/user/ReferralCodeInput';
import { useToast } from '@/hooks/use-toast';
import { usePractitionerInfo } from '@/hooks/usePractitionerInfo';
import { useMembershipStatus } from '@/hooks/useMembershipStatus';
import { Badge } from '@/components/ui/badge';
import { Award, Calendar, AlertCircle } from 'lucide-react';

const EnhancedProfilePage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, signOut } = useAuth();
  const {
    profile,
    loading,
    saving,
    wordCount,
    maxCharacters,
    saveProfile,
    deleteAccount,
    updateProfile,
  } = useProfileManagement();
  const { practitionerInfo, isLoading: practitionerLoading } = usePractitionerInfo();
  const { membershipStatus, isLoading: membershipLoading } = useMembershipStatus();

  const defaultTab = searchParams.get('tab') || 'biography';
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [referralCode, setReferralCode] = useState<string>('');
  const [applyingReferral, setApplyingReferral] = useState(false);

  // Get referral discount from practitioner info
  const referralDiscount = practitionerInfo?.discount_percentage || null;

  const handleSignOut = async () => {
    console.log('🔥 Profile page sign out button clicked!');
    try {
      console.log('🔥 Calling signOut function from profile page...');
      await signOut();
      console.log('🔥 Profile page sign out completed successfully');
      window.location.href = '/auth';
    } catch (error) {
      console.error('🔥 Profile page sign out error:', error);
      window.location.href = '/auth';
    }
  };

  // Fetch referral discount percentage if user has a referral code applied
  const handleInsertText = (text: string) => {
    // Append text to the end of the biography
    const currentBio = profile?.biography || '';
    const separator = currentBio && !currentBio.endsWith(' ') && !currentBio.endsWith('\n') ? ' ' : '';
    updateProfile({ biography: currentBio + separator + text });
  };

  const handleApplyReferralCode = async (code: string, codeId: string | null, discountPercentage?: number) => {
    if (!codeId || !code) return;

    setApplyingReferral(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/referrals/apply`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: code.trim().toUpperCase() }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to apply referral code');
      }

      // Build success message with benefits
      let description = "You've been connected with your referring practitioner.";
      if (discountPercentage && discountPercentage > 0) {
        description += ` You'll receive ${discountPercentage}% off your sessions!`;
      }

      toast({
        title: "Referral Code Applied!",
        description,
      });

      // Refresh the page to show updated referral status
      window.location.reload();
    } catch (error) {
      toast({
        title: "Failed to Apply Code",
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: "destructive",
      });
    } finally {
      setApplyingReferral(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen wellness-soft-gradient flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto opacity-70" />
          <p className="text-muted-foreground font-light">Loading your profile...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen wellness-soft-gradient">
      {/* Header */}
      <div className="meditation-card border-none shadow-none bg-card/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/app')}
            className="meditation-button mr-4 rounded-full font-light text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center space-x-3">
            <BookOpen className="h-5 w-5 text-primary opacity-80" />
            <h1 className="text-xl font-light text-foreground">Your Profile</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
           <TabsList className="grid w-full grid-cols-2 max-w-2xl mx-auto bg-card/50 backdrop-blur-sm border border-border/50 rounded-2xl p-1">
             <TabsTrigger
               value="biography"
               className="rounded-xl font-light data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-sm flex items-center gap-2"
             >
               <BookOpen className="h-4 w-4" />
               Biography
             </TabsTrigger>
             <TabsTrigger
               value="account"
               className="rounded-xl font-light data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-sm flex items-center gap-2"
             >
              <Settings className="h-4 w-4" />
              Account
            </TabsTrigger>
          </TabsList>


          <TabsContent value="biography" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <BiographyEditor
                  biography={profile?.biography || ''}
                  setBiography={(biography) => updateProfile({ biography })}
                  saving={saving}
                  onSave={saveProfile}
                  wordCount={wordCount}
                  maxCharacters={maxCharacters}
                />
              </div>
              
              <div className="lg:col-span-1 space-y-6">
                <SmartWritingAssistant 
                  biography={profile.biography || ''}
                  onInsertText={handleInsertText}
                />
                <WritingPrompts maxCharacters={maxCharacters} />
                <BiographyQualityPanel
                  biography={profile.biography || ''}
                  onBiographyChange={(optimized) => updateProfile({ biography: optimized })}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="account" className="space-y-6">
            {/* Membership Status Section */}
            {!membershipLoading && membershipStatus && (
              <Card className="meditation-card border-border/50 bg-card/80 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center text-foreground font-medium">
                    <Award className="h-5 w-5 mr-3 text-primary opacity-80" />
                    Membership Status
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Grace Period Warning Banner */}
                  {membershipStatus.in_grace_period && membershipStatus.days_until_membership_loss !== null && (
                    <div className={`p-4 rounded-xl border ${
                      membershipStatus.days_until_membership_loss <= 3
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                        : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                    }`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          membershipStatus.days_until_membership_loss <= 3
                            ? 'bg-red-100 dark:bg-red-900/40'
                            : 'bg-amber-100 dark:bg-amber-900/40'
                        }`}>
                          <AlertCircle className={`h-5 w-5 ${
                            membershipStatus.days_until_membership_loss <= 3
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-amber-600 dark:text-amber-400'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <h4 className={`font-medium ${
                            membershipStatus.days_until_membership_loss <= 3
                              ? 'text-red-800 dark:text-red-300'
                              : 'text-amber-800 dark:text-amber-300'
                          }`}>
                            {membershipStatus.days_until_membership_loss <= 1
                              ? 'Final Warning: Membership Expires Tomorrow!'
                              : membershipStatus.days_until_membership_loss <= 3
                                ? `Urgent: Only ${membershipStatus.days_until_membership_loss} Days Left!`
                                : `${membershipStatus.days_until_membership_loss} Days to Renew`
                            }
                          </h4>
                          <p className={`text-sm mt-1 ${
                            membershipStatus.days_until_membership_loss <= 3
                              ? 'text-red-700 dark:text-red-400'
                              : 'text-amber-700 dark:text-amber-400'
                          }`}>
                            Your subscription has expired. Renew now to keep your "Member since {membershipStatus.member_since_year}" status and community sharing privileges.
                          </p>
                          <Button
                            onClick={() => navigate('/pricing')}
                            size="sm"
                            className={`mt-3 ${
                              membershipStatus.days_until_membership_loss <= 3
                                ? 'bg-red-600 hover:bg-red-700'
                                : 'bg-amber-600 hover:bg-amber-700'
                            }`}
                          >
                            Renew Subscription
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {membershipStatus.is_active_member ? (
                    <div className="flex items-center justify-between p-4 bg-primary/5 rounded-xl border border-primary/20">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <Award className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-foreground capitalize">
                              {membershipStatus.membership_tier} Member
                            </span>
                            <Badge variant="outline" className={
                              membershipStatus.in_grace_period
                                ? "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700"
                                : "bg-primary/10 text-primary border-primary/20"
                            }>
                              {membershipStatus.in_grace_period ? 'Grace Period' : 'Active'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                            <Calendar className="h-3.5 w-3.5" />
                            <span>Member since {membershipStatus.member_since_year}</span>
                            {membershipStatus.continuous_months && membershipStatus.continuous_months > 0 && (
                              <span className="ml-2">
                                ({membershipStatus.continuous_months} month{membershipStatus.continuous_months !== 1 ? 's' : ''} continuous)
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-muted/30 rounded-xl border border-border/50">
                      <p className="text-muted-foreground text-sm">
                        Upgrade to a Basic or Pro subscription to become a member and unlock exclusive benefits like preferred referrals.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Card className="meditation-card border-border/50 bg-card/80 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-foreground font-medium">
                  <Settings className="h-5 w-5 mr-3 text-primary opacity-80" />
                  Account Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-6">
                  {/* Personal Information Section */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-foreground flex items-center">
                        <User className="h-5 w-5 mr-2 text-primary opacity-80" />
                        Personal Information
                      </h3>
                      <Button
                        onClick={saveProfile}
                        disabled={saving}
                        size="sm"
                        className="meditation-button rounded-xl font-light"
                      >
                        {saving ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4 mr-2" />
                        )}
                        {saving ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="first_name" className="text-sm font-medium text-foreground">
                          First Name
                        </Label>
                        <Input
                          id="first_name"
                          value={profile?.first_name || ''}
                          onChange={(e) => updateProfile({ first_name: e.target.value })}
                          placeholder="Enter your first name"
                          className="meditation-input rounded-xl border-border/50"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="last_name" className="text-sm font-medium text-foreground">
                          Last Name
                        </Label>
                        <Input
                          id="last_name"
                          value={profile?.last_name || ''}
                          onChange={(e) => updateProfile({ last_name: e.target.value })}
                          placeholder="Enter your last name"
                          className="meditation-input rounded-xl border-border/50"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="preferred_name" className="text-sm font-medium text-foreground">
                          Preferred Name <span className="text-muted-foreground">(optional)</span>
                        </Label>
                        <Input
                          id="preferred_name"
                          value={profile?.preferred_name || ''}
                          onChange={(e) => updateProfile({ preferred_name: e.target.value })}
                          placeholder="How would you like to be addressed?"
                          className="meditation-input rounded-xl border-border/50"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="name_usage" className="text-sm font-medium text-foreground">
                          Name Usage in Sessions
                        </Label>
                        <Select
                          value={profile?.name_usage_preference || 'first'}
                          onValueChange={(value) => updateProfile({ name_usage_preference: value })}
                        >
                          <SelectTrigger className="meditation-input rounded-xl border-border/50">
                            <SelectValue placeholder="Choose how your name appears" />
                          </SelectTrigger>
                          <SelectContent className="meditation-card border-border/50 rounded-xl">
                            <SelectItem value="first">Use First Name</SelectItem>
                            <SelectItem value="preferred">Use Preferred Name</SelectItem>
                            <SelectItem value="random">Mix First & Preferred</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="p-3 rounded-xl bg-muted/20 border border-border/30">
                      <p className="text-xs text-muted-foreground">
                        <strong>Preview:</strong> In your personalized sessions, you'll be addressed as{' '}
                        {profile?.name_usage_preference === 'preferred' && profile?.preferred_name
                          ? `"${profile.preferred_name}"`
                          : profile?.name_usage_preference === 'random' && profile?.preferred_name
                          ? `"${profile.first_name}" or "${profile.preferred_name}"`
                          : `"${profile?.first_name || 'there'}"`}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-foreground">Account Information</h3>
                    <div className="p-4 rounded-xl bg-muted/30 border border-border/30">
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-muted-foreground">Email:</span>
                          <span className="text-sm text-foreground">{user?.email || 'Not available'}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-muted-foreground">User ID:</span>
                          <span className="text-xs text-foreground font-mono">{user?.id || 'Not available'}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-muted-foreground">Account Created:</span>
                          <span className="text-sm text-foreground">
                            {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'Not available'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Referral Code Section */}
                  {!profile?.referral_code_id && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-foreground">Referral Code</h3>
                      <p className="text-sm text-muted-foreground font-light">
                        Have a referral code from a practitioner? Enter it here to get connected and earn benefits.
                      </p>
                      <div className="p-4 rounded-xl bg-muted/30 border border-border/30">
                        <ReferralCodeInput
                          value={referralCode}
                          onChange={setReferralCode}
                          onValidationChange={(isValid, codeId, discountPercentage) => {
                            if (isValid && codeId) {
                              handleApplyReferralCode(referralCode, codeId, discountPercentage || undefined);
                            }
                          }}
                          disabled={applyingReferral}
                          placeholder="Enter your referral code"
                          showLabel={false}
                        />
                        {applyingReferral && (
                          <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Applying referral code...</span>
                          </div>
                        )}
                        <p className="mt-3 text-xs text-muted-foreground">
                          <strong>Note:</strong> You can only apply a referral code once. Make sure it's correct before confirming.
                        </p>
                      </div>
                    </div>
                  )}

                  {profile?.referral_code_id && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-foreground">Referral Status</h3>
                      <div className="p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center">
                            <svg className="h-5 w-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <div className="flex-1 space-y-2">
                            <p className="text-sm font-medium text-green-900 dark:text-green-100">
                              Referral Code Applied
                            </p>
                            <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                              You're connected with a practitioner.
                              {referralDiscount && referralDiscount > 0 && (
                                <span className="font-semibold"> You're receiving {referralDiscount}% off all sessions!</span>
                              )}
                              {!referralDiscount && <span> Enjoy your benefits!</span>}
                            </p>

                            {practitionerLoading ? (
                              <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                <span>Loading practitioner info...</span>
                              </div>
                            ) : practitionerInfo ? (
                              <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-700 space-y-1.5">
                                <div className="flex items-center gap-2 text-sm text-green-800 dark:text-green-200">
                                  <User className="h-4 w-4" />
                                  <span className="font-medium">{practitionerInfo.full_name || 'Practitioner'}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
                                  <Mail className="h-4 w-4" />
                                  <a
                                    href={`mailto:${practitionerInfo.email}`}
                                    className="hover:underline"
                                  >
                                    {practitionerInfo.email}
                                  </a>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-foreground">Account Actions</h3>
                    <p className="text-sm text-muted-foreground font-light">
                      Sign out of your account or manage your session.
                    </p>
                    
                     <Button 
                       onClick={handleSignOut}
                       variant="outline" 
                       className="meditation-button w-full rounded-xl font-light flex items-center justify-center gap-2"
                     >
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </Button>
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-destructive">Danger Zone</h3>
                  <p className="text-sm text-muted-foreground font-light">
                    Once you delete your account, there is no going back. Please be certain.
                  </p>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="destructive" 
                        className="meditation-button w-full rounded-xl font-light"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Account
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="meditation-card border-border/50 bg-card/95 backdrop-blur-md rounded-2xl">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-foreground font-medium">Are you absolutely sure?</AlertDialogTitle>
                        <AlertDialogDescription className="text-muted-foreground font-light">
                          This action cannot be undone. This will permanently delete your account
                          and remove all of your data from our servers, including your profile,
                          session history, and preferences.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="meditation-button rounded-xl font-light">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={deleteAccount}
                          className="meditation-button bg-destructive hover:bg-destructive/90 rounded-xl font-light"
                        >
                          Yes, delete my account
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EnhancedProfilePage;